import React, { useState, useRef, useEffect } from 'react';
import { Camera, Image, Sparkles, Grid, Diamond, Film, ChevronDown, Check } from 'lucide-react';
import VirtualStudio from './components/VirtualStudio';
import Editor from './components/Editor';
import Generator from './components/Generator';
import Catalog from './components/Catalog';
import Cinema from './components/Cinema';
import { StoreProvider, useStore } from './context/StoreContext';

enum View {
  STUDIO = 'studio',
  EDITOR = 'editor',
  GENERATOR = 'generator',
  CATALOG = 'catalog',
  CINEMA = 'cinema',
}

const VIEW_CONFIG: Record<View, { label: string; icon: React.ElementType; description: string }> = {
  [View.CATALOG]: { label: 'Catalog', icon: Grid, description: 'Manage inventory & assets' },
  [View.STUDIO]: { label: 'Virtual Studio', icon: Camera, description: 'Generate product photography' },
  [View.EDITOR]: { label: 'Magic Editor', icon: Sparkles, description: 'Retouch & modify images' },
  [View.GENERATOR]: { label: 'Concept Lab', icon: Image, description: 'Create new designs' },
  [View.CINEMA]: { label: 'Lumina Cinema', icon: Film, description: 'Generate video motion' },
};

const AppContent: React.FC = () => {
  const { 
    products, 
    categories, 
    activeProduct, 
    isLoadingData, 
    addProduct, 
    updateProduct, 
    deleteProduct, 
    addCategory, 
    setActiveProduct,
    saveGeneratedAsset
  } = useStore();

  const [currentView, setCurrentView] = useState<View>(View.CATALOG); 
  const [isNavOpen, setIsNavOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsNavOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNavigateToStudio = (product?: import('./types').Product) => {
    setActiveProduct(product || null);
    setCurrentView(View.STUDIO);
  };

  const CurrentIcon = VIEW_CONFIG[currentView].icon;

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 animate-in fade-in duration-700">
          <div className="w-12 h-12 bg-stone-900 rounded-xl flex items-center justify-center text-white shadow-xl">
             <Diamond className="w-6 h-6 animate-pulse" />
          </div>
          <p className="text-stone-500 font-serif tracking-wide text-sm">Initializing Lumina...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFC] flex flex-col font-sans selection:bg-stone-200 selection:text-stone-900">
      <div className="fixed inset-0 z-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
      
      <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-stone-100 shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div 
            className="flex items-center space-x-3 cursor-pointer group" 
            onClick={() => setCurrentView(View.CATALOG)}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-stone-800 to-black rounded-xl flex items-center justify-center text-white shadow-lg shadow-stone-900/20 transition-all duration-300 group-hover:shadow-stone-900/40 group-hover:scale-105 group-hover:-rotate-3 border border-stone-700">
               <Diamond className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-serif font-bold tracking-tight text-stone-900 leading-none">Lumina</h1>
              <span className="text-[10px] text-stone-400 font-medium tracking-widest uppercase">AI Studio</span>
            </div>
          </div>
          
          <div className="relative" ref={navRef}>
            <button 
                onClick={() => setIsNavOpen(!isNavOpen)}
                className={`flex items-center space-x-4 px-1 py-1 pr-4 rounded-full transition-all duration-300 border ${isNavOpen ? 'bg-white border-stone-200 shadow-md ring-4 ring-stone-50' : 'bg-stone-50/50 hover:bg-white border-transparent hover:border-stone-200 hover:shadow-sm'}`}
            >
                <div className="p-2.5 bg-white rounded-full shadow-sm border border-stone-100">
                   <CurrentIcon className="w-4 h-4 text-stone-900" />
                </div>
                <div className="text-left hidden sm:block">
                    <span className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-0.5">Current View</span>
                    <span className="block text-sm font-bold text-stone-900 leading-none">{VIEW_CONFIG[currentView].label}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform duration-300 ${isNavOpen ? 'rotate-180' : ''}`} />
            </button>

            {isNavOpen && (
                <div className="absolute top-full right-0 mt-3 w-80 bg-white rounded-3xl shadow-2xl shadow-stone-200/50 border border-stone-100 p-3 animate-in slide-in-from-top-4 fade-in duration-200 z-50">
                    <div className="space-y-1">
                        {Object.values(View).map((view) => {
                            const config = VIEW_CONFIG[view];
                            const Icon = config.icon;
                            const isActive = currentView === view;
                            return (
                                <button
                                    key={view}
                                    onClick={() => {
                                        setCurrentView(view);
                                        if (view !== View.STUDIO) setActiveProduct(null);
                                        setIsNavOpen(false);
                                    }}
                                    className={`w-full flex items-center p-3 rounded-2xl text-left transition-all duration-200 group ${
                                        isActive 
                                        ? 'bg-stone-50 text-stone-900 ring-1 ring-stone-200 shadow-inner' 
                                        : 'text-stone-500 hover:bg-stone-50 hover:text-stone-900'
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 transition-colors ${isActive ? 'bg-white shadow-sm border border-stone-100 text-stone-900' : 'bg-stone-100 text-stone-400 group-hover:bg-white group-hover:shadow-sm'}`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-0.5">
                                            <span className="font-bold text-sm">{config.label}</span>
                                            {isActive && <Check className="w-4 h-4 text-stone-900" />}
                                        </div>
                                        <p className="text-xs text-stone-400 font-medium">{config.description}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 pt-28 pb-12 px-4 sm:px-6 lg:px-8 z-10 animate-in fade-in duration-700 slide-in-from-bottom-4">
        <div className="max-w-7xl mx-auto">
          {currentView === View.STUDIO && (
            <VirtualStudio 
              activeProduct={activeProduct}
              onSave={saveGeneratedAsset} 
            />
          )}
          {currentView === View.EDITOR && (
            <Editor 
              onSave={(url, prompt) => saveGeneratedAsset(url, 'edited', prompt)}
              activeProduct={activeProduct}
            />
          )}
          {currentView === View.GENERATOR && (
            <Generator 
              onSave={(url, prompt) => saveGeneratedAsset(url, 'generated', prompt)} 
            />
          )}
          {currentView === View.CINEMA && (
            <Cinema 
              onSave={(url, style) => saveGeneratedAsset(url, 'video', style)} 
            />
          )}
          {currentView === View.CATALOG && (
            <Catalog 
              products={products}
              categories={categories}
              onAddProduct={addProduct}
              onUpdateProduct={updateProduct}
              onDeleteProduct={deleteProduct}
              onAddCategory={addCategory}
              onNavigateToStudio={handleNavigateToStudio}
            />
          )}
        </div>
      </main>

      <footer className="bg-white border-t border-stone-100 py-12 mt-auto z-10">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex justify-center items-center space-x-2 mb-4 opacity-40 grayscale hover:grayscale-0 transition-all duration-500">
             <Diamond className="w-4 h-4" />
             <span className="font-serif font-bold text-stone-900">Lumina</span>
          </div>
          <p className="text-stone-400 text-xs font-medium tracking-wide">© 2024 Lumina Virtual Jewelry. Powered by Gemini.</p>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <StoreProvider>
      <AppContent />
    </StoreProvider>
  );
};

export default App;
